/* ============================================================
   YUKI BOOKING v4 – CocoDry architecture + Shopify Checkout
   ============================================================ */
(function () {
  'use strict';

  const CFG = window.YUKI_BOOKING || {};
  const API = CFG.supabaseUrl + '/rest/v1';
  const HEADERS = {
    apikey: CFG.supabaseKey, Authorization: 'Bearer ' + CFG.supabaseKey,
    'Content-Type': 'application/json', Prefer: 'return=representation'
  };

  /* ------------------------------------------------------------
     IMAGERY LIVES IN THE THEME, NOT THE DATABASE.

     This is now the documented and only source of service imagery.
     The Supabase catalogue has no image_url column on either
     service_categories or services and one is not planned: the salon
     owner edits photographs through the theme editor, which is a
     far better fit than a database table for a non-technical user.

     The section supplies `categoryMedia` keyed by the Supabase
     category name, built from image_picker settings so the client can
     change any photograph without a developer, falling back to the
     curated Shopify Files defaults when a picker is left empty.

     Categories with no genuine photograph of their treatment fall to
     the pastel monogram, keyed to the category exactly as the team
     cards are. No emoji is used anywhere.
     ------------------------------------------------------------ */
  const MEDIA = CFG.categoryMedia || {};
  const MONOGRAM = CFG.monogram || '';

  /* --- i18n (Pass i18n-P3). BK_LOCALE is stamped by booking-page.liquid from
     request.locale. Service names: ja shows name_jp, zh shows name_zh with
     fallback to name (rule-1: original language when untranslated). --- */
  const LOC = (window.BK_LOCALE || 'en').indexOf('ja') === 0 ? 'ja'
            : (window.BK_LOCALE || '').indexOf('zh') === 0 ? 'zh' : 'en';
  /* Locale root for every composed URL ('/', '/ja/', '/zh/'), injected by
     theme.liquid. Without it, cart and checkout hops drop the chosen
     language back to EN. */
  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const TIER_LABELS = {
    en: { senior: 'Senior Stylist', top: 'Top Stylist', japanese: 'Japanese Stylist' },
    ja: { senior: 'シニアスタイリスト', top: 'トップスタイリスト', japanese: '日本人スタイリスト' },
    zh: { senior: '资深美容师', top: '首席美容师', japanese: '日籍美容师' }
  };
  const TIER_LABEL = TIER_LABELS[LOC];
  const T = {
    en: { any: 'Any stylist', byLevel: 'By stylist level', choose: 'Choose a stylist',
          processing: 'Processing...', loadFail: 'Unable to load treatments. Please refresh or message us on WhatsApp.',
          pickTime: 'Pick an available time.' },
    ja: { any: 'スタイリスト指定なし', byLevel: 'スタイリストランクによる', choose: 'スタイリストをお選びください',
          processing: '処理中...', loadFail: 'メニューを読み込めませんでした。再読み込みするか、WhatsAppでご連絡ください。',
          pickTime: '空いている時間をお選びください。' },
    zh: { any: '不指定美容师', byLevel: '按美容师等级', choose: '请选择美容师',
          processing: '处理中...', loadFail: '无法加载服务项目。请刷新页面或通过WhatsApp联系我们。',
          pickTime: '请选择可预约的时间。' }
  }[LOC];
  function svcName(x) {
    if (!x) return '';
    if (LOC === 'ja') return x.name_jp || x.name;
    if (LOC === 'zh') return x.name_zh || x.name;
    return x.name;
  }
  function svcSub(x) {
    if (!x) return '';
    if (LOC === 'ja') return x.name;          /* device inverted */
    if (LOC === 'zh') return x.name_zh ? x.name : (x.name_jp || '');
    return x.name_jp || '';
  }

  /* Popular treatments. Rule, stated in WORKLOG: the lowest-priced
     core treatment in each of six families, one per family, covering
     all four brand pillars plus the house Fortunebrows method.
     Add-ons, per-nail items, removals, touch-ups and multi-session
     packages are excluded, since those are not entry points. */
  const POPULAR = [
    'Classic Manicure',
    'Gel Polish',
    'Single Eyelash Style (50-60 pcs)',
    'Fortunebrows Eyebrow Design',
    'Stem Cells Facial Treatment',
    'Eyeliner (Free 1 Touch Up)'
  ];

  const ICON = {
    tick: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6L9 17l-5-5"/></svg>',
    star: '<svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path d="M10 1.5l2.6 5.3 5.9.9-4.2 4.1 1 5.8L10 15l-5.3 2.8 1-5.8L1.5 7.7l5.9-.9z"/></svg>',
    info: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M12 11v5M12 7.6v.6"/></svg>'
  };

  const state = {
    step: 1, categories: [], services: [], staff: [], staffServices: [],
    businessHours: [], blockedDates: [], existingBookings: [],
    selectedService: null, selectedArea: null, selectedStaff: null, selectedDate: null, selectedTime: null,
    customerName: '', customerPhone: '', customerEmail: '', customerNotes: '',
    calMonth: new Date().getMonth(), calYear: new Date().getFullYear(),
    query: '', activeCat: null
  };

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }
  function catOf(id) { return state.categories.find(c => c.id === id) || {}; }
  function mediaFor(cat) { return MEDIA[cat && cat.name] || {}; }
  function toneFor(cat) { return mediaFor(cat).tone || 'taupe'; }

  /* Card media: category image, then the pastel monogram.
     The former `svc.image_url` branch was removed: that column does not exist
     on `services`, so the read was always undefined and only made the fallback
     chain look like it had a step it never had. Per-service photography would
     need either a new column or a per-service theme setting, and neither is
     warranted while the salon has one photograph per treatment family. */
  function thumbHtml(svc, cat, cls) {
    const tone = toneFor(cat);
    const m = mediaFor(cat);
    const src = m.image || null;
    const inner = src
      ? `<img src="${esc(src)}" alt="${esc(svc ? svc.name : cat.name)}" loading="lazy">`
      : `<img class="bk-mark" src="${esc(MONOGRAM)}" alt="" loading="lazy">`;
    return `<div class="${cls} bk-tone-${tone}">${inner}</div>`;
  }

  const $steps = document.querySelectorAll('.booking-step');
  const $progress = document.querySelectorAll('.booking-progress__step');
  const $lines = document.querySelectorAll('.booking-progress__line');
  const $btnBack = document.getElementById('btn-back');
  const $btnNext = document.getElementById('btn-next');

  async function supaGet(t, q) {
    const r = await fetch(API + '/' + t + '?' + (q || ''), { headers: HEADERS });
    if (!r.ok) throw new Error('API ' + r.status);
    return r.json();
  }
  async function supaPost(t, d) {
    const r = await fetch(API + '/' + t, { method: 'POST', headers: HEADERS, body: JSON.stringify(d) });
    if (!r.ok) throw new Error('POST ' + r.status);
    return r.json();
  }

  /* --- Tier pricing (stylist tier: senior / top / japanese) --- */
  function staffTierFor(sid) {
    const st = state.staff.find(x => x.id === sid);
    return st ? st.tier : null;
  }
  function resolvedTier() {
    if (state.selectedStaff && state.selectedStaff !== 'any') return state.selectedStaff.tier || null;
    if (state.selectedStaff === 'any' && state._resolvedStaffId) return staffTierFor(state._resolvedStaffId);
    return null;
  }
  function tierMin(node) {
    return (node && typeof node === 'object') ? Math.min(node.hand, node.feet) : node;
  }
  function pickTierKey(pricing, tier) {
    if (!pricing) return null;
    if (tier && pricing[tier] != null) return tier;
    const keys = Object.keys(pricing);
    if (!keys.length) return null;
    // Fallback (e.g. a Top-tier stylist on a Japanese-only Facial): cheapest available tier
    return keys.sort((a, b) => tierMin(pricing[a]) - tierMin(pricing[b]))[0];
  }
  // Returns { price, variantId, resolved } for a service given a tier + area.
  function servicePrice(svc, tier, area) {
    if (!svc) return { price: 0, variantId: null, resolved: false };
    const p = svc.pricing;
    if (!p) return { price: Number(svc.price_myr), variantId: svc.shopify_variant_id, resolved: false };
    const tk = pickTierKey(p, tier);
    const node = p[tk];
    const vnode = svc.shopify_variants ? svc.shopify_variants[tk] : null;
    if (svc.has_hand_feet) {
      const a = area || 'hand';
      return {
        price: node ? node[a] : Number(svc.price_myr),
        variantId: vnode ? vnode[a] : svc.shopify_variant_id,
        resolved: !!tier, area: a, tier: tk
      };
    }
    return {
      price: node != null ? node : Number(svc.price_myr),
      variantId: vnode || svc.shopify_variant_id,
      resolved: !!tier, tier: tk
    };
  }
  function fmtPrice(n) { return 'RM ' + Number(n).toFixed(2); }
  /* Whole-ringgit form for catalogue display. Every catalogue price is an
     integer, so decimals only add noise on the browse screens. The payment
     figures keep fmtPrice. */
  function fmtMoney(n) { return 'RM ' + Number(n).toLocaleString('en-MY'); }

  /* The true span of a service across tiers, and across Hand/Feet when no
     area is chosen yet. Computed from `pricing`, never from `price_label`:
     several labels carry a trailing 〜 or a range that the data does not
     support (Color Lash is labelled "RM 50〜70" but prices at a flat 50,
     Stone Nail is labelled "RM 5〜" but is a flat 5). */
  function priceRange(svc, area) {
    const p = svc && svc.pricing;
    const flat = Number(svc && svc.price_myr) || 0;
    if (!p) return { lo: flat, hi: flat, tiers: [] };
    const vals = [];
    const tiers = Object.keys(p);
    tiers.forEach(k => {
      const node = p[k];
      if (node && typeof node === 'object') {
        if (area) { if (node[area] != null) vals.push(Number(node[area])); }
        else Object.keys(node).forEach(a => { if (node[a] != null) vals.push(Number(node[a])); });
      } else if (node != null) vals.push(Number(node));
    });
    if (!vals.length) return { lo: flat, hi: flat, tiers: tiers };
    return { lo: Math.min.apply(null, vals), hi: Math.max.apply(null, vals), tiers: tiers };
  }

  /* What a browse card should say about price, and why it varies. */
  function cardPrice(svc) {
    const area = (state.selectedService && state.selectedService.id === svc.id) ? state.selectedArea : null;
    const r = priceRange(svc, area);
    if (r.lo === r.hi) {
      return {
        amount: fmtMoney(r.lo),
        note: r.tiers.length > 1 ? T.any : (TIER_LABEL[r.tiers[0]] || '')
      };
    }
    return { amount: fmtMoney(r.lo) + ' – ' + fmtMoney(r.hi), note: T.byLevel };
  }

  function tierLabel(t) { return TIER_LABEL[t] || ''; }

  /* `currentPriceLabel()` was removed here. It was already unreachable, having
     been superseded by priceState(), and it held the last read of
     `services.price_label` anywhere in the theme. Several of those labels
     contradict the `pricing` jsonb they claim to describe (Color Lash is
     labelled "RM 50〜70" and prices flat at 50), so removing the function makes
     the guarantee structural rather than incidental: nothing in this codebase
     can surface a price_label to a customer. See WORKLOG, pending database
     cleanup, for the full list of contradictions. */

  /* Price plus its status, so the UI can distinguish a confirmed figure from
     an estimate instead of showing both the same way. */
  function priceState() {
    const s = state.selectedService;
    if (!s) return { label: 'RM 0.00', confirmed: false, note: '' };
    const t = resolvedTier();
    if (t) {
      const info = servicePrice(s, t, state.selectedArea);
      return {
        label: fmtPrice(info.price),
        confirmed: true,
        note: tierLabel(info.tier) + ' price'
      };
    }
    const r = priceRange(s, state.selectedArea);
    if (r.lo !== r.hi) {
      return { label: fmtMoney(r.lo) + ' – ' + fmtMoney(r.hi), confirmed: false, note: T.choose };
    }
    return { label: fmtMoney(r.lo), confirmed: false, note: T.any };
  }

  async function init() {
    try {
      const [cats, svcs, staff, ss, hours, blocked] = await Promise.all([
        supaGet('service_categories', 'order=sort_order'),
        supaGet('services', 'is_active=eq.true&order=sort_order'),
        supaGet('staff', 'bookable_online=eq.true&order=sort_order'),
        supaGet('staff_services', ''),
        supaGet('business_hours', ''),
        supaGet('blocked_dates', 'blocked_date=gte.' + todayStr())
      ]);
      Object.assign(state, { categories: cats, services: svcs, staff, staffServices: ss, businessHours: hours, blockedDates: blocked });
      renderPopular();
      renderCategoryTabs();
      renderChips();
      renderGroupedServices();
      wireSearch();
      renderReview();
      applyDeepLink();
    } catch (e) {
      console.error('init', e);
      document.getElementById('services-grouped').innerHTML =
        '<p class="bk-error">' + T.loadFail + '</p>';
    }
  }

  /* --- Popular treatments row --- */
  function renderPopular() {
    const $el = document.getElementById('bk-popular-grid');
    if (!$el) return;
    const picks = POPULAR
      .map(n => state.services.find(s => s.name === n))
      .filter(Boolean);
    if (!picks.length) {
      const wrap = document.getElementById('bk-popular');
      if (wrap) wrap.style.display = 'none';
      return;
    }
    $el.innerHTML = picks.map(s => {
      const cat = catOf(s.category_id);
      const cp = cardPrice(s);
      const sel = state.selectedService && state.selectedService.id === s.id;
      return `
        <button type="button" class="bk-pop-card${sel ? ' selected' : ''}" onclick="window._selectService('${s.id}', true)">
          ${thumbHtml(s, cat, 'bk-pop-card__img')}
          <span class="bk-pop-card__body">
            <span class="bk-pop-card__name">${esc(svcName(s))}</span>
            <span class="bk-pop-card__price">${cp.amount}</span>
          </span>
        </button>`;
    }).join('');
  }

  /* --- Live search across name and name_jp --- */
  function wireSearch() {
    const $in = document.getElementById('bk-search-input');
    const $clear = document.getElementById('bk-search-clear');
    if (!$in) return;
    $in.addEventListener('input', () => {
      state.query = $in.value.trim();
      if ($clear) $clear.classList.toggle('is-visible', state.query.length > 0);
      renderGroupedServices();
    });
    if ($clear) $clear.addEventListener('click', () => {
      $in.value = '';
      state.query = '';
      $clear.classList.remove('is-visible');
      renderGroupedServices();
      $in.focus();
    });
  }

  /* Matches the service name and name_jp, plus its category's name and
     name_jp. The category is included because the everyday Japanese terms
     live only there: まつ毛 and アートメイク appear in
     service_categories.name_jp and in no service name_jp at all, so a
     Japanese client searching the obvious word for eyelash would otherwise
     get an empty list. */
  function matchesQuery(s) {
    const q = state.query.toLowerCase();
    if (!q) return true;
    const cat = catOf(s.category_id);
    return [s.name, s.name_jp, cat.name, cat.name_jp]
      .some(v => String(v || '').toLowerCase().includes(q));
  }

  /* --- Sidebar --- */
  function renderSidebar() {
    const $sel = document.getElementById('sidebar-selections');
    const $total = document.getElementById('sidebar-total');
    const rows = [];
    if (state.selectedService) {
      rows.push(['Treatment', state.selectedService.name, state.selectedService.name_jp]);
    }
    if (state.selectedService && state.selectedService.has_hand_feet && state.selectedArea) {
      rows.push(['Area', state.selectedArea === 'feet' ? 'Feet' : 'Hand', state.selectedArea === 'feet' ? 'フット' : 'ハンド']);
    }
    if (state.selectedStaff) {
      const n = state.selectedStaff === 'any' ? T.any : state.selectedStaff.name;
      rows.push(['Stylist', n, '']);
    }
    if (state.selectedDate) {
      rows.push(['Date', new Date(state.selectedDate + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }), '']);
    }
    if (state.selectedTime) rows.push(['Time', fmtTime(state.selectedTime), '']);

    $sel.innerHTML = rows.length === 0
      ? '<div class="sidebar-empty">Select a treatment to begin</div>'
      : rows.map(r => `<div class="sidebar-item"><span class="sidebar-item__label">${esc(r[0])}</span><span class="sidebar-item__value">${esc(r[1])}${r[2] ? `<small>${esc(r[2])}</small>` : ''}</span></div>`).join('');

    const ps = priceState();
    $total.textContent = ps.label;
    const $state = document.getElementById('sidebar-total-state');
    if ($state) $state.textContent = ps.note;
    const $wrap = document.getElementById('sidebar-total-wrap');
    if ($wrap) $wrap.classList.toggle('sidebar-total--confirmed', ps.confirmed);
  }

  /* --- Sidebar review: a real Google review, matched to the category ---
     Copy comes from the section settings, which carry the same verbatim
     reviews already published on /pages/reviews. Nothing is written here. */
  function renderReview() {
    const $el = document.getElementById('bk-review');
    if (!$el) return;
    const all = CFG.reviews || [];
    if (!all.length) { $el.style.display = 'none'; return; }
    const cat = state.selectedService ? catOf(state.selectedService.category_id).name : null;
    let r = null;
    if (cat) r = all.find(x => (x.match || '').split('|').some(m => m && cat.toLowerCase().includes(m.toLowerCase())));
    if (!r) r = all[0];
    const isJP = /[぀-ヿ一-龯]/.test(r.quote || '');
    $el.style.display = '';
    $el.innerHTML = `
      <div class="bk-review__stars" aria-label="5 out of 5 stars">${ICON.star.repeat(5)}</div>
      <p class="bk-review__quote"${isJP ? ' lang="ja"' : ''}>${esc(r.quote)}</p>
      <p class="bk-review__foot">${esc(r.name)}${r.treatment ? ' · ' + esc(r.treatment) : ''} · Google</p>`;
  }

  /* --- Navigation --- */
  function goToStep(n) {
    state.step = n;
    $steps.forEach(s => s.classList.toggle('active', +s.dataset.step === n));
    $progress.forEach(p => {
      const ps = +p.dataset.step;
      p.classList.toggle('active', ps === n);
      p.classList.toggle('completed', ps < n);
    });
    $lines.forEach((l, i) => l.classList.toggle('filled', i + 1 < n));
    $btnBack.style.display = n > 1 && n < 5 ? '' : 'none';
    $btnNext.style.display = n === 5 ? 'none' : '';
    updateNextBtn();
    if (n === 2) renderStaff();
    if (n === 3) renderCalendar();
    if (n === 5) renderSummary();
    window.scrollTo({ top: document.querySelector('.booking-section').offsetTop - 40, behavior: 'smooth' });
  }

  function updateNextBtn() {
    let ok = false;
    if (state.step === 1) ok = !!state.selectedService && (!state.selectedService.has_hand_feet || !!state.selectedArea);
    if (state.step === 2) ok = !!state.selectedStaff;
    if (state.step === 3) ok = !!state.selectedDate && !!state.selectedTime;
    if (state.step === 4) ok = !!document.getElementById('b-name')?.value.trim() && !!document.getElementById('b-phone')?.value.trim();
    $btnNext.disabled = !ok;
    renderSidebar();
  }

  $btnNext.addEventListener('click', () => {
    if (state.step === 4) {
      state.customerName = document.getElementById('b-name').value.trim();
      state.customerPhone = document.getElementById('b-phone').value.trim();
      state.customerEmail = document.getElementById('b-email').value.trim();
      state.customerNotes = document.getElementById('b-notes').value.trim();
    }
    if (state.step < 5) goToStep(state.step + 1);
  });
  $btnBack.addEventListener('click', () => { if (state.step > 1) goToStep(state.step - 1); });

  /* --- Step 1: Category tabs + grouped services --- */
  function renderCategoryTabs() {
    const $tabs = document.getElementById('category-tabs');
    // The strip is a filter, not a scroll-spy. Rendering all 64 cards at once
    // made step 1 nearly nine thousand pixels tall; showing one category at a
    // time cuts that to roughly a screen and a half. Nothing is hidden: an
    // explicit "All treatments" tab still lists the full catalogue, and search
    // always spans every category regardless of which tab is active.
    if (!state.activeCat) state.activeCat = state.categories.length ? state.categories[0].id : 'all';
    const total = state.services.length;
    const allTab = `<button class="cat-tab${state.activeCat === 'all' ? ' active' : ''}" data-cat="all" onclick="window._selectCategory('all', this)">All treatments<span class="cat-tab__count">${total}</span></button>`;
    $tabs.innerHTML = allTab + state.categories.map(c => {
      const n = state.services.filter(s => s.category_id === c.id).length;
      return `<button class="cat-tab${state.activeCat === c.id ? ' active' : ''}" data-cat="${c.id}" onclick="window._selectCategory('${c.id}', this)">${esc(c.name)}<span class="cat-tab__count">${n}</span></button>`;
    }).join('');
    document.getElementById('cat-scroll-left').onclick = () => $tabs.scrollBy({ left: -180, behavior: 'smooth' });
    document.getElementById('cat-scroll-right').onclick = () => $tabs.scrollBy({ left: 180, behavior: 'smooth' });
  }

  /* --- Booking deep-links ------------------------------------------------
     /pages/book?service=<uuid | name-slug>            pre-selects the service
     /pages/book?category=<uuid | name-slug | family>  opens that category tab
     family keys: nail | artmakeup | eyelash | facials
     service wins when both are present. The wizard advances to step 2 only
     when nothing on step 1 is left to choose (a hand/feet service stays on
     step 1 with the area toggle visible). Unknown values are ignored and the
     page behaves exactly like bare /pages/book. Scheme is documented in
     dashboard-app/DECISIONS.md and consumed by the advisor's booking links. */
  function slugify(s) { return String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, ''); }
  function applyDeepLink() {
    let p;
    try { p = new URLSearchParams(window.location.search); } catch (e) { return; }
    const sv = p.get('service'), cv = p.get('category');
    if (sv) {
      const hit = state.services.find(s => s.id === sv) ||
                  state.services.find(s => slugify(s.name) === slugify(sv)) ||
                  state.services.find(s => s.name.toLowerCase() === sv.toLowerCase());
      if (hit) {
        window._selectService(hit.id, true);
        if (!hit.has_hand_feet) goToStep(2);
        return;
      }
    }
    if (cv) {
      const FAMILY = { nail: 'nail', artmakeup: 'art', 'art-makeup': 'art',
                       eyelash: 'eyelash', lash: 'eyelash', facials: 'facial', facial: 'facial' };
      const key = FAMILY[cv.toLowerCase()];
      const hit = state.categories.find(c => c.id === cv) ||
                  state.categories.find(c => slugify(c.name) === slugify(cv)) ||
                  (key ? state.categories.find(c => c.name.toLowerCase().includes(key)) : null);
      if (hit) {
        window._selectCategory(hit.id, null);
        const tab = document.querySelector('.cat-tab[data-cat="' + hit.id + '"]');
        if (tab) tab.classList.add('active');
        const g = document.getElementById('group-' + hit.id);
        if (g) g.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }

  window._selectCategory = function (id, btn) {
    state.activeCat = id;
    document.querySelectorAll('.cat-tab').forEach(t => t.classList.remove('active'));
    if (btn) btn.classList.add('active');
    // A category change while searching would give a confusing partial result,
    // so picking a tab clears the query and returns to browsing.
    const $in = document.getElementById('bk-search-input');
    if (state.query && $in) {
      $in.value = '';
      state.query = '';
      document.getElementById('bk-search-clear')?.classList.remove('is-visible');
    }
    renderGroupedServices();
  };
  // Retained: the old name is referenced by nothing else, but keeping the alias
  // means a cached copy of the previous markup cannot throw.
  window._jumpToCategory = window._selectCategory;

  function renderGroupedServices() {
    const $el = document.getElementById('services-grouped');
    const searching = state.query.length > 0;

    // The popular row is an entry shortcut, not a filter result. Hide it while
    // searching so the visible list is exactly what matched.
    const $pop = document.getElementById('bk-popular');
    if ($pop) $pop.style.display = searching ? 'none' : '';

    // Search always spans the whole catalogue. Browsing shows the active tab.
    const cats = (searching || state.activeCat === 'all')
      ? state.categories
      : state.categories.filter(c => c.id === state.activeCat);

    let shown = 0;
    const groups = cats.map(cat => {
      const svcs = state.services.filter(s => s.category_id === cat.id).filter(matchesQuery);
      if (!svcs.length) return '';
      shown += svcs.length;
      const tone = toneFor(cat);
      return `
        <div class="service-group" id="group-${cat.id}">
          <h3 class="service-group__heading">
            ${esc(cat.name)}
            <span class="service-group__jp">${esc(LOC === 'ja' ? cat.name : cat.name_jp)}</span>
            <span class="service-group__count">${svcs.length} treatment${svcs.length === 1 ? '' : 's'}</span>
          </h3>
          <div class="service-cards">
            ${svcs.map(s => {
              const sel = state.selectedService?.id === s.id;
              const cp = cardPrice(s);
              const areaToggle = (sel && s.has_hand_feet) ? areaToggleHtml(s) : '';
              return `
              <div class="service-card-h${sel ? ' selected' : ''}" onclick="window._selectService('${s.id}')">
                <span class="service-card-h__tick" aria-hidden="true">${ICON.tick}</span>
                ${thumbHtml(s, cat, 'service-card-h__img')}
                <div class="service-card-h__info">
                  <h5>${esc(svcName(s))}</h5>
                  <span class="jp">${esc(svcSub(s))}</span>
                  <span class="dur">${s.duration_minutes} min</span>
                  ${areaToggle}
                </div>
                <div class="service-card-h__price">
                  <span class="service-card-h__amount">${cp.amount}</span>
                  ${cp.note ? `<span class="service-card-h__tiernote">${esc(cp.note)}</span>` : ''}
                </div>
              </div>
            `;}).join('')}
          </div>
        </div>`;
    }).join('');

    const status = searching
      ? `<p class="bk-search-status">${shown} treatment${shown === 1 ? '' : 's'} matching &ldquo;${esc(state.query)}&rdquo;</p>`
      : '';

    // A dead end at the search box is a lost booking, so the empty state
    // carries a way back into the catalogue and a way to reach a human.
    $el.innerHTML = shown === 0
      ? `<div class="bk-empty">
           <p class="bk-empty__line">Nothing matches &ldquo;${esc(state.query)}&rdquo;.</p>
           <p class="bk-empty__sub">Try a shorter word, or browse the full menu.</p>
           <div class="bk-empty__actions">
             <button type="button" class="bk-empty__browse" onclick="window._clearSearchBrowseAll()">Browse all treatments</button>
             <a class="bk-empty__wa" href="${waHref('Hello, I am looking for a treatment and could not find it on your booking page. Could you help?')}" target="_blank" rel="noopener">Ask us on WhatsApp</a>
           </div>
         </div>`
      : status + groups;
  }

  /* Clears the query and shows the whole catalogue in one tap. */
  window._clearSearchBrowseAll = function () {
    const $in = document.getElementById('bk-search-input');
    if ($in) $in.value = '';
    state.query = '';
    document.getElementById('bk-search-clear')?.classList.remove('is-visible');
    state.activeCat = 'all';
    renderCategoryTabs();
    renderChips();
    renderGroupedServices();
    document.querySelector('.category-tabs-wrap')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  /* ------------------------------------------------------------
     Mobile category chips.

     On a 390px viewport the sticky tab strip sits below the title, the effort
     line, six stacked popular cards and the search field, so a returning
     client scrolls a long way just to pick a category. These chips sit at the
     very top on mobile only. They share `_selectCategory`, so there is one
     source of truth for the active category and no second state to drift.
     Hidden at desktop widths, where the tab strip is already above the fold.
     ------------------------------------------------------------ */
  function renderChips() {
    const $el = document.getElementById('bk-chips');
    if (!$el) return;
    const total = state.services.length;
    const chips = [`<button type="button" class="bk-chip${state.activeCat === 'all' ? ' is-active' : ''}" onclick="window._chipSelect('all', this)">All <span class="bk-chip__n">${total}</span></button>`]
      .concat(state.categories.map(c => {
        const n = state.services.filter(s => s.category_id === c.id).length;
        return `<button type="button" class="bk-chip${state.activeCat === c.id ? ' is-active' : ''}" onclick="window._chipSelect('${c.id}', this)">${esc(c.name)} <span class="bk-chip__n">${n}</span></button>`;
      }));
    $el.innerHTML = chips.join('');
  }

  window._chipSelect = function (id) {
    window._selectCategory(id, null);
    renderCategoryTabs();
    renderChips();
    // Land the reader on the list rather than leaving them at the top.
    document.querySelector('.category-tabs-wrap')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  /* Hand or Feet changes what you pay, so each option carries its own price. */
  function areaToggleHtml(s) {
    const tier = resolvedTier();
    function areaPrice(a) {
      if (tier) return fmtMoney(servicePrice(s, tier, a).price);
      const r = priceRange(s, a);
      return r.lo === r.hi ? fmtMoney(r.lo) : fmtMoney(r.lo) + ' – ' + fmtMoney(r.hi);
    }
    return `
      <div class="service-area-toggle" onclick="event.stopPropagation();">
        <span class="service-area-toggle__label">Choose area, this changes the price</span>
        <button type="button" class="area-btn${state.selectedArea === 'hand' ? ' active' : ''}" onclick="window._selectArea('hand')">
          Hand<span class="area-btn__jp">ハンド</span><span class="area-btn__price">${areaPrice('hand')}</span>
        </button>
        <button type="button" class="area-btn${state.selectedArea === 'feet' ? ' active' : ''}" onclick="window._selectArea('feet')">
          Feet<span class="area-btn__jp">フット</span><span class="area-btn__price">${areaPrice('feet')}</span>
        </button>
      </div>`;
  }

  window._selectService = function (id, fromPopular) {
    state.selectedService = state.services.find(s => s.id === id);
    state.selectedArea = state.selectedService && state.selectedService.has_hand_feet ? 'hand' : null;
    state.selectedStaff = null; state.selectedDate = null; state.selectedTime = null;
    state._resolvedStaffId = null;
    // A popular pick is one tap. Switch the strip to that treatment's category
    // so the selection is visible in context rather than filtered out of view.
    if (fromPopular && state.selectedService) {
      state.activeCat = state.selectedService.category_id;
      state.query = '';
      const $in = document.getElementById('bk-search-input');
      if ($in) $in.value = '';
      document.getElementById('bk-search-clear')?.classList.remove('is-visible');
      renderCategoryTabs();
      renderChips();
    }
    renderGroupedServices();
    renderPopular();
    renderReview();
    updateNextBtn();
    if (fromPopular && state.selectedService) {
      const g = document.getElementById('group-' + state.selectedService.category_id);
      if (g) g.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  window._selectArea = function (a) {
    state.selectedArea = a;
    renderGroupedServices();
    renderPopular();
    updateNextBtn();
  };

  /* --- Step 2: Staff --- */
  function renderStaff() {
    const $el = document.getElementById('staff-list');
    const ids = state.staffServices.filter(x => x.service_id === state.selectedService.id).map(x => x.staff_id);
    const avail = state.staff.filter(s => ids.includes(s.id));
    const svc = state.selectedService;
    // Each card shows what that stylist actually costs for this treatment, so
    // the tier is a visible price choice rather than a hidden one.
    function priceFor(st) {
      if (!svc) return '';
      const info = servicePrice(svc, st ? st.tier : null, state.selectedArea);
      return `<span class="staff-card__price">${fmtMoney(info.price)}</span>
              <span class="staff-card__tier">${esc(tierLabel(info.tier) || 'From')}</span>`;
    }
    const anyRange = svc ? priceRange(svc, state.selectedArea) : null;
    const anyLabel = anyRange
      ? (anyRange.lo === anyRange.hi ? fmtMoney(anyRange.lo) : fmtMoney(anyRange.lo) + ' – ' + fmtMoney(anyRange.hi))
      : '';

    $el.innerHTML = `
      <div class="staff-card staff-card--any${state.selectedStaff === 'any' ? ' selected' : ''}" onclick="window._selectStaff('any')">
        <div class="staff-card__photo"><img class="bk-mark" src="${esc(MONOGRAM)}" alt="" loading="lazy"></div>
        <h4>No Preference</h4><p class="staff-card__role">おまかせ</p>
        ${anyLabel ? `<span class="staff-card__price">${anyLabel}</span><span class="staff-card__tier">Whoever is free</span>` : ''}
      </div>
      ${avail.map(s => `
        <div class="staff-card${state.selectedStaff?.id === s.id ? ' selected' : ''}" onclick="window._selectStaff('${s.id}')">
          <div class="staff-card__photo">${s.photo_url ? `<img src="${esc(s.photo_url)}" alt="${esc(s.name)}" loading="lazy">` : esc(s.name.charAt(0))}</div>
          <h4>${esc(s.name)}</h4><p class="staff-card__role">${esc(s.role || '')}</p>
          ${priceFor(s)}
        </div>`).join('')}`;
  }

  window._selectStaff = function (id) {
    if (id === 'any') {
      state.selectedStaff = 'any';
      state._availableStaffIds = state.staffServices.filter(x => x.service_id === state.selectedService.id).map(x => x.staff_id);
    } else {
      state.selectedStaff = state.staff.find(s => s.id === id);
      state._availableStaffIds = [id];
    }
    state.selectedDate = null; state.selectedTime = null;
    renderStaff(); updateNextBtn();
  };

  /* ------------------------------------------------------------
     Empty availability state.

     A date with no slots was a dead end: one sentence and nothing to act on.
     It now offers the nearest dates that do have availability, plus WhatsApp.

     IMPORTANT: this is a READ-ONLY probe. `renderSlots()` is deliberately not
     touched, so availability resolution itself is unchanged and stays Tier 1.
     The probe mirrors the same four rules renderSlots applies (business hours
     for the day, staff not blocked, no booking overlap, 30 minute grid). If
     those rules ever change in renderSlots, change them here too.
     ------------------------------------------------------------ */
  function probeSlotCount(ds, dur, staffIds, bookings) {
    const dow = new Date(ds + 'T00:00:00').getDay();
    let count = 0;
    const seen = new Set();
    staffIds.forEach(sid => {
      const h = state.businessHours.find(x => x.staff_id === sid && x.day_of_week === dow);
      if (!h || !h.is_available) return;
      if (state.blockedDates.find(b => b.staff_id === sid && b.blocked_date === ds)) return;
      const st = t2m(h.start_time), en = t2m(h.end_time);
      const bk = bookings.filter(b => b.staff_id === sid && b.booking_date === ds);
      for (let t = st; t + dur <= en; t += 30) {
        const ts = m2t(t);
        const conflict = bk.some(b => t < t2m(b.end_time) && t + dur > t2m(b.start_time));
        if (!conflict && !seen.has(ts)) { seen.add(ts); count++; }
      }
    });
    return count;
  }

  async function findAlternativeDates(fromDs, want) {
    const dur = state.selectedService.duration_minutes;
    const ids = state._availableStaffIds || [];
    if (!ids.length) return [];
    const start = new Date(fromDs + 'T00:00:00');
    const horizon = 28;
    const end = new Date(start); end.setDate(end.getDate() + horizon);
    const endStr = fmtDate(end);
    let bookings = [];
    try {
      // one range query rather than one per candidate date
      bookings = await supaGet('bookings',
        `booking_date=gte.${fromDs}&booking_date=lte.${endStr}` +
        `&status=in.(pending,confirmed)&select=staff_id,booking_date,start_time,end_time`);
    } catch (e) { bookings = []; }

    const today = new Date(); today.setHours(0, 0, 0, 0);
    const found = [];
    for (let i = 1; i <= horizon && found.length < want; i++) {
      const d = new Date(start); d.setDate(d.getDate() + i);
      if (d < today) continue;
      const ds = fmtDate(d);
      if (probeSlotCount(ds, dur, ids, bookings) > 0) found.push(ds);
    }
    return found;
  }

  function waHref(msg) {
    return 'https://wa.me/' + (CFG.whatsappNumber || '60162053149') + '?text=' + encodeURIComponent(msg);
  }

  function emptySlotsHtml() {
    // Scheduled from inside this function rather than from renderSlots, so the
    // availability resolver stays byte-identical. The timeout lets the returned
    // markup land in the DOM before the fill looks for its container.
    const ds = state.selectedDate;
    if (ds) setTimeout(() => fillAlternativeDates(ds), 0);
    return `
      <div class="bk-empty" id="bk-empty-slots">
        <p class="bk-empty__line">Fully booked on this date.</p>
        <div class="bk-empty__alts" id="bk-alt-dates">
          <span class="bk-empty__searching">Finding the nearest available dates...</span>
        </div>
        <a class="bk-empty__wa" href="${waHref('Hello, I would like to book ' + (state.selectedService ? state.selectedService.name : 'a treatment') + ' but the dates I want are full. Could you suggest a time?')}" target="_blank" rel="noopener">
          Ask us on WhatsApp
        </a>
      </div>`;
  }

  /* Fills the alternatives in after the empty state has rendered, so the
     message appears immediately and the probe never blocks the UI. */
  async function fillAlternativeDates(fromDs) {
    const $el = document.getElementById('bk-alt-dates');
    if (!$el) return;
    const alts = await findAlternativeDates(fromDs, 3);
    if (!document.getElementById('bk-alt-dates')) return;
    if (!alts.length) {
      $el.innerHTML = '<span class="bk-empty__searching">No openings in the next four weeks.</span>';
      return;
    }
    $el.innerHTML = '<span class="bk-empty__label">Nearest availability</span>' +
      alts.map(ds => {
        const d = new Date(ds + 'T00:00:00');
        const label = d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
        return `<button type="button" class="bk-alt" onclick="window._selectDate('${ds}')">${esc(label)}</button>`;
      }).join('');
  }

  /* --- Step 3: Calendar --- */
  function renderCalendar() {
    const y = state.calYear, m = state.calMonth;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    document.getElementById('cal-month-label').textContent =
      new Date(y, m).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    const first = new Date(y, m, 1).getDay();
    const days = new Date(y, m + 1, 0).getDate();
    let html = '';
    for (let i = 0; i < first; i++) html += '<button class="cal-day empty" disabled></button>';
    for (let d = 1; d <= days; d++) {
      const dt = new Date(y, m, d), ds = fmtDate(dt);
      const past = dt < today, dow = dt.getDay();
      const blocked = isBlocked(ds, dow);
      const dis = past || blocked;
      html += `<button class="cal-day${dis ? ' disabled' : ''}${dt.getTime() === today.getTime() ? ' today' : ''}${state.selectedDate === ds ? ' selected' : ''}" ${dis ? 'disabled' : `onclick="window._selectDate('${ds}')"`}>${d}</button>`;
    }
    document.getElementById('calendar-grid').innerHTML = html;
    if (!state.selectedDate) {
      document.getElementById('timeslots-label').textContent = 'Select a date';
      document.getElementById('timeslots').innerHTML = '';
    }
  }

  function isBlocked(ds, dow) {
    const ids = state._availableStaffIds || [];
    return ids.every(sid => {
      const h = state.businessHours.find(x => x.staff_id === sid && x.day_of_week === dow);
      if (!h || !h.is_available) return true;
      return !!state.blockedDates.find(b => b.staff_id === sid && b.blocked_date === ds);
    });
  }

  document.getElementById('cal-prev').addEventListener('click', () => {
    state.calMonth--; if (state.calMonth < 0) { state.calMonth = 11; state.calYear--; } renderCalendar();
  });
  document.getElementById('cal-next').addEventListener('click', () => {
    state.calMonth++; if (state.calMonth > 11) { state.calMonth = 0; state.calYear++; } renderCalendar();
  });

  window._selectDate = async function (ds) {
    state.selectedDate = ds; state.selectedTime = null;
    renderCalendar(); await renderSlots(ds); updateNextBtn();
  };

  async function renderSlots(ds) {
    const dow = new Date(ds + 'T00:00:00').getDay();
    const dur = state.selectedService.duration_minutes;
    const ids = state._availableStaffIds || [];
    document.getElementById('timeslots-label').textContent =
      new Date(ds + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
    try {
      state.existingBookings = await supaGet('bookings',
        `booking_date=eq.${ds}&status=in.(pending,confirmed)&select=staff_id,start_time,end_time`);
    } catch (e) { state.existingBookings = []; }

    const slots = new Map();
    ids.forEach(sid => {
      const h = state.businessHours.find(x => x.staff_id === sid && x.day_of_week === dow);
      if (!h || !h.is_available) return;
      if (state.blockedDates.find(b => b.staff_id === sid && b.blocked_date === ds)) return;
      const st = t2m(h.start_time), en = t2m(h.end_time);
      const bk = state.existingBookings.filter(b => b.staff_id === sid);
      for (let t = st; t + dur <= en; t += 30) {
        const ts = m2t(t);
        const conflict = bk.some(b => t < t2m(b.end_time) && t + dur > t2m(b.start_time));
        if (!conflict && !slots.has(ts)) slots.set(ts, { time: ts, staffId: sid });
      }
    });

    const now = new Date(), isToday = ds === todayStr();
    const sorted = Array.from(slots.values())
      .filter(s => !isToday || t2m(s.time) >= now.getHours() * 60 + now.getMinutes() + 30)
      .sort((a, b) => a.time.localeCompare(b.time));

    document.getElementById('timeslots').innerHTML = sorted.length === 0
      ? emptySlotsHtml()
      : sorted.map(s => `<button class="timeslot${state.selectedTime === s.time ? ' selected' : ''}" onclick="window._selectTime('${s.time}','${s.staffId}')">${fmtTime(s.time)}</button>`).join('');
  }

  window._selectTime = function (t, sid) {
    state.selectedTime = t;
    if (state.selectedStaff === 'any') state._resolvedStaffId = sid;
    document.querySelectorAll('.timeslot').forEach(el =>
      el.classList.toggle('selected', el.textContent === fmtTime(t)));
    updateNextBtn();
  };

  document.addEventListener('input', e => {
    if (['b-name', 'b-phone', 'b-email', 'b-notes'].includes(e.target.id)) updateNextBtn();
  });

  /* --- Step 5: Summary + checkout --- */
  function renderSummary() {
    const s = state.selectedService;
    const sid = state.selectedStaff === 'any' ? state._resolvedStaffId : state.selectedStaff?.id;
    const st = state.staff.find(x => x.id === sid);
    const dd = new Date(state.selectedDate + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
    const td = fmtTime(state.selectedTime);
    const et = m2t(t2m(state.selectedTime) + s.duration_minutes);
    const priceInfo = servicePrice(s, st ? st.tier : resolvedTier(), state.selectedArea);
    const priceStr = fmtPrice(priceInfo.price);

    document.getElementById('booking-summary').innerHTML = `
      <div class="summary-row"><span class="summary-row__label">Treatment</span><span class="summary-row__value">${s.name}<small>${s.name_jp}</small></span></div>
      ${s.has_hand_feet ? `<div class="summary-row"><span class="summary-row__label">Area</span><span class="summary-row__value">${state.selectedArea === 'feet' ? 'Feet' : 'Hand'}</span></div>` : ''}
      <div class="summary-row"><span class="summary-row__label">Stylist</span><span class="summary-row__value">${st?.name || 'Any available'}</span></div>
      <div class="summary-row"><span class="summary-row__label">Date</span><span class="summary-row__value">${dd}</span></div>
      <div class="summary-row"><span class="summary-row__label">Time</span><span class="summary-row__value">${td} – ${fmtTime(et)}<small>${s.duration_minutes} min</small></span></div>
      <div class="summary-row"><span class="summary-row__label">Name</span><span class="summary-row__value">${state.customerName}</span></div>
      <div class="summary-row"><span class="summary-row__label">Phone</span><span class="summary-row__value">${state.customerPhone}</span></div>
      ${state.customerEmail ? `<div class="summary-row"><span class="summary-row__label">Email</span><span class="summary-row__value">${state.customerEmail}</span></div>` : ''}
      ${state.customerNotes ? `<div class="summary-row"><span class="summary-row__label">Notes</span><span class="summary-row__value">${state.customerNotes}</span></div>` : ''}
      <div class="summary-total">
        <span>Total</span>
        <span>
          <span class="price">${priceStr}</span>
          <span class="summary-total__state">${esc(tierLabel(priceInfo.tier))} price, confirmed</span>
        </span>
      </div>`;

    const areaLine = s.has_hand_feet ? ` [${state.selectedArea === 'feet' ? 'Feet' : 'Hand'}]` : '';
    // Plain labelled lines. The brand voice takes no exclamation marks and the
    // emoji list did not survive the v2 copy rules.
    const wa = encodeURIComponent(`Hello, I have booked at ${CFG.salonName}.\n\nTreatment: ${s.name}${areaLine} (${s.name_jp})\nStylist: ${st?.name || 'Any available'}\nDate: ${dd}\nTime: ${td}\nTotal: ${priceStr}\n\n${state.customerName} / ${state.customerPhone}\nよろしくお願いします。`);

    document.getElementById('booking-actions').innerHTML = `
      <button class="btn-checkout" id="btn-checkout" onclick="window._checkout()">Proceed to Payment</button>
      <a href="https://wa.me/${CFG.whatsappNumber}?text=${wa}" target="_blank" rel="noopener" class="btn-whatsapp-secondary">
        <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true"><path fill="currentColor" d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path fill="currentColor" d="M12 0C5.373 0 0 5.373 0 12c0 2.625.846 5.059 2.284 7.034L.789 23.492a.5.5 0 00.611.611l4.458-1.495A11.943 11.943 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-2.319 0-4.47-.75-6.216-2.026l-.435-.327-2.648.888.888-2.648-.327-.435A9.96 9.96 0 012 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z"/></svg>
        Also message us on WhatsApp</a>`;
  }

  window._checkout = async function () {
    const s = state.selectedService;
    const sid = state.selectedStaff === 'any' ? state._resolvedStaffId : state.selectedStaff?.id;
    const st = state.staff.find(x => x.id === sid);
    const et = m2t(t2m(state.selectedTime) + s.duration_minutes);
    const dd = new Date(state.selectedDate + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' });

    const $b = document.getElementById('btn-checkout');
    $b.disabled = true; $b.textContent = T.processing;

    /* The booking id is minted CLIENT-SIDE because RLS grants public INSERT only:
       PostgREST cannot return the generated id without a SELECT policy, and adding
       one would let anyone read bookings. The same id goes into the cart as the
       _booking_id line item property, which the orders/paid webhook uses to mark
       the booking paid. Underscore prefix keeps it out of customer-facing cart and
       checkout displays. If the insert fails the property still flows, and the
       webhook stores the order with booking_id null, which is the safe outcome. */
    const bookingId = (window.crypto && window.crypto.randomUUID) ? window.crypto.randomUUID() : null;
    try {
      await supaPost('bookings', Object.assign(bookingId ? { id: bookingId } : {}, {
        service_id: s.id, staff_id: sid, booking_date: state.selectedDate,
        start_time: state.selectedTime, end_time: et,
        customer_name: state.customerName, customer_phone: state.customerPhone,
        customer_email: state.customerEmail || null, notes: state.customerNotes || null,
        status: 'pending'
      }));
    } catch (e) { console.error('supabase', e); }

    try { await fetch(ROOT + 'cart/clear.js', { method: 'POST', headers: { 'Content-Type': 'application/json' } }); } catch (e) {}

    const info = servicePrice(s, st ? st.tier : resolvedTier(), state.selectedArea);
    const variantId = info.variantId || s.shopify_variant_id;
    if (!variantId) { showSuccess(); return; }

    const props = {
      'Booking Date': dd, 'Booking Time': fmtTime(state.selectedTime) + ' – ' + fmtTime(et),
      'Staff': st?.name || 'Any available', 'Duration': s.duration_minutes + ' min',
      'Customer Name': state.customerName, 'Customer Phone': state.customerPhone
    };
    if (s.has_hand_feet) props['Area'] = state.selectedArea === 'feet' ? 'Feet' : 'Hand';
    if (bookingId) props['_booking_id'] = bookingId;

    try {
      const r = await fetch(ROOT + 'cart/add.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: parseInt(variantId), quantity: 1,
          properties: props
        })
      });
      if (!r.ok) throw new Error('cart');
      window.location.href = ROOT + 'checkout';
    } catch (e) { console.error('cart', e); showSuccess(); }
  };

  function showSuccess() {
    document.getElementById('booking-summary').style.display = 'none';
    document.getElementById('booking-actions').style.display = 'none';
    document.getElementById('booking-success').style.display = 'block';
    document.getElementById('booking-sidebar').style.display = 'none';
  }

  /* --- Utils --- */
  function todayStr() { const d = new Date(); return fmtDate(d); }
  function fmtDate(d) { return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0'); }
  function t2m(t) { const p = t.split(':'); return +p[0] * 60 + +p[1]; }
  function m2t(m) { return String(Math.floor(m / 60)).padStart(2, '0') + ':' + String(m % 60).padStart(2, '0'); }
  function fmtTime(t) {
    const [h, m] = t.split(':'); const hr = +h;
    return (hr > 12 ? hr - 12 : (hr === 0 ? 12 : hr)) + ':' + m + ' ' + (hr >= 12 ? 'PM' : 'AM');
  }

  if (document.getElementById('booking-app')) init();
})();
